import type { Identifier } from "ra-core";

import type { TaskKind } from "../types";

export type { TaskKind };

/** List filter: every task, internal (team) tasks only, or client tasks only. */
export type TaskKindFilter = "all" | TaskKind;

/** react-admin store key of the Todas / Interna / Cliente list filter. */
export const TASK_KIND_FILTER_STORE_KEY = "tasks.kindFilter";

export const TASK_KINDS: readonly TaskKind[] = ["internal", "client"];
export const TASK_KIND_FILTERS: readonly TaskKindFilter[] = [
  "all",
  "internal",
  "client",
];

interface TaskKindLike {
  kind?: TaskKind | null;
  contact_id?: Identifier | null;
}

/**
 * Kind of a task. Records loaded before the `kind` column existed (offline
 * cache, old demo data) have no kind: they are client tasks if they have a
 * contact.
 */
export const getTaskKind = (task: TaskKindLike): TaskKind =>
  task.kind ?? (task.contact_id != null ? "client" : "internal");

export const isInternalTask = (task: TaskKindLike): boolean =>
  getTaskKind(task) === "internal";

export const filterTasksByKind = <T extends TaskKindLike>(
  tasks: readonly T[],
  filter: TaskKindFilter,
): T[] =>
  filter === "all"
    ? [...tasks]
    : tasks.filter((task) => getTaskKind(task) === filter);

export const isTaskKindFilter = (value: unknown): value is TaskKindFilter =>
  TASK_KIND_FILTERS.includes(value as TaskKindFilter);

/**
 * Merges the current user's tasks with the team's internal tasks (assigned
 * to anyone), without duplicates, keeping the due date order.
 */
export const mergeTaskLists = <
  T extends { id: Identifier; due_date?: string | null },
>(
  ...lists: readonly (readonly T[] | undefined)[]
): T[] => {
  const byId = new Map<Identifier, T>();
  for (const list of lists) {
    for (const task of list ?? []) byId.set(task.id, task);
  }
  const time = (task: T) =>
    task.due_date ? new Date(task.due_date).getTime() : Infinity;
  // `|| 0`: two undated tasks give Infinity - Infinity = NaN.
  return [...byId.values()].sort((a, b) => time(a) - time(b) || 0);
};

/** In-app page of a task: its contact, or the Tareas list for internal ones. */
export const taskHref = (task: TaskKindLike): string =>
  task.contact_id != null ? `/contacts/${task.contact_id}/show` : "/tasks";
